import React, { useState } from 'react';
import { ActionItem } from '../../../libs/gs-component-form';
import { PhotoUploading } from '.';
import PhotoUploadRow from './PhotoUploadRow';
import styles from './upload.scss';
import { ImageMetadata } from '../../../helpers/images/mediaApiUtils';
import Icon from '@xo-union/tk-component-icons';
import { imageUrlToImgElement } from '../../../helpers/images/processImages';
import PhotoCropper from '../../Modals/PhotoCropper';
import { CropProps } from '../ImageField';
import { useDeletePhotoItemMutation } from '../../../generated/graphql';
import deleteTimelinePhoto from '../../../helpers/apolloCache/deleteTimelinePhoto';
import Honeybadger from 'honeybadger-js';
import { pagesQuery } from '../../LeftContainer/graphql';

interface Props {
  pageId: number;
  timelineId?: number;
  uploads: PhotoUploading[];
  onChange: (uploads: PhotoUploading[]) => void;
  onError: () => void;
}

interface CropperImage {
  dataUrl: string;
  mediaMetadata?: ImageMetadata;
  index: number;
}

const UploadsList: React.FC<Props> = ({ pageId, timelineId, uploads, onChange, onError }) => {
  const [cropperPhoto, setCropperPhoto] = useState<CropperImage & { img: HTMLImageElement }>();
  const [deletePhoto] = useDeletePhotoItemMutation();
  const [activeRow, setActiveRow] = useState(0);

  const onMetadataChange = (mediaMetadata: ImageMetadata, index: number) => {
    const newUploads = [...uploads];
    newUploads[index] = {
      ...newUploads[index],
      mediaMetadata,
    };

    onChange(newUploads);
  };

  const openCropper = async (photo: CropperImage) => {
    setCropperPhoto({
      ...photo,
      img: await imageUrlToImgElement(photo.dataUrl),
    });
  };

  const closeCropper = () => {
    setCropperPhoto(undefined);
  };

  const saveCrop = async ({ crop, rotation }: CropProps) => {
    if (!cropperPhoto) return;

    onMetadataChange(
      {
        ...cropperPhoto.mediaMetadata,
        ...crop,
        rotation,
      },
      cropperPhoto.index
    );

    closeCropper();
  };

  const onDelete = async (index: number, id?: number) => {
    if (timelineId && id) {
      try {
        await deletePhoto({
          variables: { attributes: { id, pageId } },
          update(cache) {
            deleteTimelinePhoto({ cache, pageId, photoTimelineId: timelineId, photoId: id });
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      } catch (error) {
        Honeybadger.notify(error, 'Error deleting Photo Timeline photo', {
          photoId: id,
          pageId,
          Component: 'UploadList',
        });
      }
    } else {
      const updatedUploads = [...uploads];
      updatedUploads.splice(index, 1);
      onChange(updatedUploads);
    }
  };

  return (
    <>
      {uploads.map(({ dataUrl, error, id, key, percentUploaded, mediaMetadata }, index) => (
        <div
          className={styles.uploadRow}
          key={key || id}
          style={{ zIndex: activeRow === index ? 10 : 'inherit' }}
          onClick={() => setActiveRow(index)}
          data-testid={`timeline-item-${index}`}
        >
          <div className={styles.imageWrapper}>
            <div
              className={styles.imageSquare}
              onClick={() =>
                openCropper({
                  dataUrl,
                  mediaMetadata,
                  index,
                })
              }
              data-testid={`crop-opener-${key || id}`}
            >
              <div className={styles.imageOverlay}>
                <Icon name="edit" size="md" className={styles.editIcon} />
              </div>
              <img alt={`Gallery Item ${index + 1}`} src={dataUrl} className={styles.image} />
            </div>
            {percentUploaded === 100 && (
              <div className={styles.delete}>
                <ActionItem type="removeBlue" onClick={() => onDelete(index, id)} data-testid={`delete-photo-${index}`}>
                  Delete
                </ActionItem>
              </div>
            )}
          </div>
          <div className={styles.uploadStatus}>
            {!error ? (
              <PhotoUploadRow
                metadata={mediaMetadata || {}}
                percentUploaded={percentUploaded}
                onChange={metadata => onMetadataChange(metadata, index)}
                onError={onError}
              />
            ) : (
              <span className={styles.uploadError}>{error.message}</span>
            )}
          </div>
        </div>
      ))}
      {cropperPhoto && (
        <PhotoCropper
          cropperInfo={{
            file: cropperPhoto.img,
            onSave: saveCrop,
          }}
          onCancel={closeCropper}
          pageId={pageId}
          itemType="PhotoItem"
          secondaryActionText="Discard Changes"
          primaryActionText="Save Changes"
          currentAspectRatio={cropperPhoto.mediaMetadata?.aspectRatio}
          defaultLayoutRatioName={cropperPhoto.mediaMetadata?.aspectRatio?.name || '1:1'}
          defaultLayoutRatioValue={cropperPhoto.mediaMetadata?.aspectRatio?.value || 1}
        />
      )}
    </>
  );
};

export default UploadsList;
